import React from 'react';
import { Alert, Button, Icon, InputGroup, Intent, ProgressBar, Spinner, SpinnerSize, Tag, Text } from '@blueprintjs/core';
import { useDispatch } from 'react-redux';
import ActivityBarIcon from '../../components/ActivityBarIcon';
import { uninstallInstalledExtension } from './uninstallExtension';
import './extensions.css';

/**
 * Icon for a locally installed extension. The manifest icon is a path relative
 * to the extension folder, so it is resolved against the folder on disk and
 * rendered by ActivityBarIcon, which handles the `file:` prefixing.
 */
const InstalledIcon = ({ record }) => {
    if (!record?.icon || !record?.dir) {
        return <Icon icon="cube" size={40} intent="secondary" />;
    }

    return (
        <ActivityBarIcon
            icon={bodapro.path.join(record.dir, record.icon)}
            size={40}
            onClick={() => {}}
        />
    );
};

const InstalledExtensions = () => {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState(null);
    const [filter, setFilter] = React.useState('');
    const [defaultExtensions, setDefaultExtensions] = React.useState([]);
    const [selected, setSelected] = React.useState(null);

    // name pending confirmation, name currently being removed
    const [confirming, setConfirming] = React.useState(null);
    const [removing, setRemoving] = React.useState(null);
    const [removeError, setRemoveError] = React.useState(null);
    const [removedName, setRemovedName] = React.useState(null);

    const dispatch = useDispatch();

    const refresh = React.useCallback(async () => {
        setLoadError(null);
        try {
            const configData = await bodapro.getConfig();
            if (!configData?.config) {
                setLoadError('Could not load BodaPro configuration.');
                setItems([]);
                return;
            }
            const records = await bodapro.listInstalledExtensions(configData.config);
            setItems(Array.isArray(records) ? records : []);
        } catch (e) {
            console.log('Could not list installed extensions', e);
            setLoadError(e.message || 'Could not read installed extensions.');
            setItems([]);
        }
    }, []);

    React.useEffect(() => {
        const fn = async () => {
            await refresh();
            try {
                const runConfig = await bodapro.getRunConfig?.() || {};
                setDefaultExtensions(runConfig.defaultExtensions || []);
            } catch (e) {
                console.log('Could not read run config', e);
            }
            setLoading(false);
        };
        fn();
    }, [refresh]);

    const openInfo = (record) => {
        const context = window['bodapro-context'];
        if (!context?.addPanel) return;

        setSelected(record.name);
        context.addPanel({
            id: 'installed-extension-info',
            title: record.label || record.name,
            icon: 'cube',
            componentName: 'installed-extension-info',
            groupId: 'main-panel',
            params: { record },
            replacePanel: true
        });
    };

    const doUninstall = async (name) => {
        setRemoving(name);
        setRemoveError(null);

        const result = await uninstallInstalledExtension(name, dispatch);

        if (result.success) {
            setRemovedName(name);
            await refresh();
        } else {
            setRemoveError(`${name}: ${result.message}`);
        }

        setRemoving(null);
    };

    const visible = items.filter(v => {
        if (!filter) return true;
        const needle = filter.toLowerCase();
        return (v.label || '').toLowerCase().includes(needle)
            || (v.name || '').toLowerCase().includes(needle)
            || (v.vendor || '').toLowerCase().includes(needle);
    });

    const confirmingIsDefault = confirming !== null && defaultExtensions.includes(confirming);

    if (loading) return <div className="p-2"><Spinner size={SpinnerSize.LARGE} /></div>;

    return (<div style={{ height: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ flex: 1, marginRight: 6 }}>
                <InputGroup
                    style={{ width: '100%' }}
                    type="search"
                    placeholder="Search installed"
                    dir="auto"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                />
            </div>
            <Button small minimal icon="refresh" title="Refresh" onClick={refresh} />
        </div>

        {removing && <ProgressBar intent={Intent.PRIMARY} />}
        {removeError && <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>{removeError}</div>}
        {loadError && <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>{loadError}</div>}

        <div style={{ overflowY: 'auto', height: 'calc(100% - 40px)' }}>
            <div style={{ marginRight: 10 }}>
                {visible.length === 0 && !loadError && (
                    <div className="bp5-text-muted p-2" style={{ fontSize: 12 }}>
                        {items.length === 0
                            ? 'No extensions installed yet.'
                            : 'No installed extensions match your search.'}
                    </div>
                )}

                {visible.map((v) => (
                    <div
                        key={v.folder}
                        onClick={() => openInfo(v)}
                        style={{ width: '100%', marginBottom: 10 }}
                        className={`extesion-search-result ${selected === v.name ? 'selected-result-item' : ''}`}
                    >
                        <div style={{ float: 'left' }} className="p-2">
                            <InstalledIcon record={v} />
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span className="bp5-heading" style={{ marginBottom: 0 }}>{v.label || v.name}</span>
                                {v.version && <Tag minimal round>{v.version}</Tag>}
                                {defaultExtensions.includes(v.name) && <Tag minimal round intent={Intent.PRIMARY}>Default</Tag>}
                            </div>

                            {v.description && <div><Text ellipsize>{v.description}</Text></div>}

                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {v.vendor && (
                                        <Text className="bp5-heading bp5-text-muted bp5-running-text mb-0">{v.vendor}</Text>
                                    )}
                                    {!v.valid && (
                                        <span className="bp5-text-muted" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Icon icon="warning-sign" size={12} intent={Intent.WARNING} />
                                            {v.error ? 'Manifest could not be read' : 'Not loaded'}
                                        </span>
                                    )}
                                </div>
                                <div style={{ flex: 1 }} />
                                <div>
                                    <Button
                                        icon="trash"
                                        small
                                        minimal
                                        intent={Intent.DANGER}
                                        title="Uninstall"
                                        disabled={removing !== null}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setConfirming(v.name);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <Alert
            cancelButtonText="Cancel"
            confirmButtonText="Uninstall"
            isOpen={confirming !== null}
            onClose={() => setConfirming(null)}
            icon="trash"
            intent={Intent.DANGER}
            onConfirm={() => {
                const name = confirming;
                setConfirming(null);
                doUninstall(name);
            }}
        >
            <p>Uninstall &quot;{confirming}&quot;? Its folder will be deleted from disk.</p>
            {confirmingIsDefault && (
                <p>This is one of this installation&apos;s default extensions.</p>
            )}
        </Alert>

        <Alert
            confirmButtonText="Reload Now"
            cancelButtonText="Later"
            isOpen={removedName !== null}
            onClose={() => setRemovedName(null)}
            onConfirm={() => { window.location.reload(); }}
            icon="cube"
            intent={Intent.PRIMARY}
        >
            <p>Extension &quot;{removedName}&quot; was uninstalled. Reload BodaPro to clear anything it left behind.</p>
        </Alert>
    </div>);
};

export default InstalledExtensions;
